import { useNavigate } from "@tanstack/react-router"
import { Button } from "@aix/ui"
import { trpc } from "~/lib/trpc"

type NextActionType =
  | "create_first_coach"
  | "complete_intake"
  | "daily_check_in"
  | "read_messages"
  | "create_weekly_plan"
  | "create_quarterly_plan"
  | "weekly_review"
  | "all_caught_up"

type UrgencyLevel = "high" | "medium" | "low" | "none"

interface NextAction {
  type: NextActionType
  title: string
  subtitle?: string
  urgency: UrgencyLevel
  cta: string
  estimatedTime?: string
  coachIds?: string[]
  coachList?: string[]
  metadata?: Record<string, unknown>
}

const urgencyStyles: Record<UrgencyLevel, { bg: string; border: string; text: string }> = {
  high: {
    bg: "bg-destructive/10",
    border: "border-destructive",
    text: "text-destructive",
  },
  medium: {
    bg: "bg-primary/10",
    border: "border-primary",
    text: "text-primary",
  },
  low: {
    bg: "bg-muted",
    border: "border-border",
    text: "text-muted-foreground",
  },
  none: {
    bg: "bg-background",
    border: "border-border",
    text: "text-foreground",
  },
}

const urgencyLabels: Record<UrgencyLevel, string> = {
  high: "🔴 High Priority",
  medium: "🟡 Medium Priority",
  low: "🟢 Low Priority",
  none: "",
}

export default function WhatsNextCard() {
  const navigate = useNavigate()
  const { data: nextAction, isLoading } = trpc.coachDashboard.getNextAction.useQuery()

  const handleActionClick = (action: NextAction) => {
    switch (action.type) {
      case "create_first_coach":
      case "all_caught_up":
        navigate({
          to: "/coach-os",
          // @ts-ignore
          search: (prev) => ({ ...prev, tab: "templates" }),
        })
        break
      case "complete_intake":
      case "create_quarterly_plan":
        if (action.coachIds && action.coachIds[0]) {
          navigate({
            to: "/coach-os/coach/$coachId",
            params: { coachId: action.coachIds[0] },
            // @ts-ignore
            search: { tab: "chat" },
          })
        }
        break
      case "daily_check_in":
        navigate({
          to: "/coach-os",
          // @ts-ignore
          search: (prev) => ({ ...prev, tab: "check-in" }),
        })
        break
      case "create_weekly_plan":
        if (action.coachIds && action.coachIds[0]) {
          navigate({
            to: "/coach-os/coach/$coachId",
            params: { coachId: action.coachIds[0] },
            // @ts-ignore
            search: { tab: "plans" },
          })
        }
        break
      case "read_messages":
        navigate({
          to: "/coach-os",
          // @ts-ignore
          search: (prev) => ({ ...prev, tab: "dashboard", scrollTo: "messages" }),
        })
        break
      case "weekly_review":
        navigate({
          to: "/coach-os",
          // @ts-ignore
          search: (prev) => ({ ...prev, tab: "weekly-review" }),
        })
        break
      default:
        // For unhandled actions, stay on dashboard
        break
    }
  }

  if (isLoading) {
    return (
      <div className="border-2 border-border rounded-lg p-6 bg-background animate-pulse">
        <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
        <div className="h-4 bg-muted rounded w-1/2 mb-6"></div>
        <div className="h-10 bg-muted rounded w-40"></div>
      </div>
    )
  }

  if (!nextAction) {
    return null
  }

  const styles = urgencyStyles[nextAction.urgency]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">📍 What&apos;s Next for You</h2>
        {urgencyLabels[nextAction.urgency] && (
          <span className={`text-xs font-medium ${styles.text}`}>{urgencyLabels[nextAction.urgency]}</span>
        )}
      </div>

      <div className={`border-2 ${styles.border} ${styles.bg} rounded-lg p-6 transition-all hover:shadow-md`}>
        <div className="space-y-4">
          {/* Title */}
          <div>
            <h3 className="text-xl font-bold text-foreground mb-2">{nextAction.title}</h3>
            {nextAction.subtitle && <p className="text-sm text-muted-foreground">{nextAction.subtitle}</p>}
          </div>

          {/* Coach list for check-ins */}
          {nextAction.coachList && nextAction.coachList.length > 0 && (
            <div className="bg-background/50 rounded-lg p-3 border border-border">
              <p className="text-xs font-medium text-muted-foreground mb-2">Coaches waiting:</p>
              <div className="flex flex-wrap gap-2">
                {nextAction.coachList.map((coachName, idx) => (
                  <span
                    key={idx}
                    className="inline-block px-2 py-1 bg-primary/10 border border-primary rounded text-xs font-medium text-primary"
                  >
                    {coachName}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action button and time estimate */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Button
              onClick={() => handleActionClick(nextAction)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
            >
              {nextAction.cta}
            </Button>

            {nextAction.estimatedTime && (
              <span className="text-xs text-muted-foreground">⏱️ Takes {nextAction.estimatedTime}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
